import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    LinearProgress,
    Alert,
    Chip,
    Divider,
    Grid,
    Card,
    CardContent
} from '@mui/material';
import { contentService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import StatusPill from '../shared/StatusPill';

const AIProcessingModal = ({ open, onClose, contentId = null, processingType = 'batch' }) => {
    const [processing, setProcessing] = useState(false);
    const [stats, setStats] = useState(null);
    const [results, setResults] = useState(null);
    const [error, setError] = useState('');
    const { showToast } = useToast();

    useEffect(() => {
        if (open) {
            loadStats();
            setResults(null);
            setError('');
        }
    }, [open]);

    const loadStats = async () => {
        try {
            const response = await contentService.getAIProcessingStats();
            setStats(response.data.stats);
        } catch (error) {
            console.error('Error loading stats:', error);
            setError('Failed to load processing statistics');
        }
    };

    const handleProcessing = async (type) => {
        setProcessing(true);
        setError('');
        setResults(null);

        try {
            let response;
            
            switch (type) {
                case 'files':
                    response = await contentService.processFiles(10);
                    break;
                case 'ai':
                    response = await contentService.processAI(5);
                    break;
                case 'complete':
                    response = await contentService.processComplete(10, 5);
                    break;
                case 'single':
                    if (contentId) {
                        response = await contentService.processCompleteForContent(contentId);
                    }
                    break;
                case 'batch':
                    response = await contentService.processCompleteBatch(5);
                    break;
                default:
                    throw new Error('Invalid processing type');
            }

            setResults(response.data);
            showToast(response.data.message || 'Processing completed successfully', 'success');
            
            // Reload stats after processing
            await loadStats();

        } catch (error) {
            console.error('Processing error:', error);
            setError(error.response?.data?.message || 'Processing failed');
            showToast('Processing failed', 'error');
        } finally {
            setProcessing(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return 'success';
            case 'processing': return 'warning';
            case 'failed': return 'error';
            case 'pending': return 'info';
            default: return 'default';
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                AI Skill Processing
                {contentId && ` - Content ID: ${contentId}`}
            </DialogTitle>
            
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Processing Statistics */}
                {stats && (
                    <Card sx={{ mb: 2 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Processing Statistics
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6} sm={3}>
                                    <Box textAlign="center">
                                        <Typography variant="h4" color="primary">
                                            {stats.total_contents}
                                        </Typography>
                                        <Typography variant="caption">
                                            Total Content
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Box textAlign="center">
                                        <Typography variant="h4" color="info.main">
                                            {stats.files_parsed}
                                        </Typography>
                                        <Typography variant="caption">
                                            Files Parsed
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Box textAlign="center">
                                        <Typography variant="h4" color="success.main">
                                            {stats.ai_skills_suggested}
                                        </Typography>
                                        <Typography variant="caption">
                                            AI Processed
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Box textAlign="center">
                                        <Typography variant="h4" color="error.main">
                                            {stats.failed}
                                        </Typography>
                                        <Typography variant="caption">
                                            Failed
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                            
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" gutterBottom>
                                    Processing Status Distribution:
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    <StatusPill status={'pending'} label={`Pending: ${stats.pending}`} />
                                    <StatusPill status={'processing'} label={`Processing: ${stats.processing}`} />
                                    <StatusPill status={'completed'} label={`Completed: ${stats.completed}`} />
                                    <StatusPill status={'failed'} label={`Failed: ${stats.failed}`} />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                )}

                {/* Processing Controls */}
                <Card sx={{ mb: 2 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Processing Actions
                        </Typography>
                        
                        {contentId ? (
                            // Single content processing
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Button
                                    variant="contained"
                                    onClick={() => handleProcessing('single')}
                                    disabled={processing}
                                    size="small"
                                >
                                    Process This Content
                                </Button>
                            </Box>
                        ) : (
                            // Batch processing options
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => handleProcessing('files')}
                                    disabled={processing}
                                    size="small"
                                >
                                    Parse Files Only
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => handleProcessing('ai')}
                                    disabled={processing}
                                    size="small"
                                >
                                    AI Processing Only
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={() => handleProcessing('batch')}
                                    disabled={processing}
                                    size="small"
                                >
                                    Complete Processing (5 items)
                                </Button>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={() => handleProcessing('complete')}
                                    disabled={processing}
                                    size="small"
                                >
                                    Full Pipeline (10 files + 5 AI)
                                </Button>
                            </Box>
                        )}
                    </CardContent>
                </Card>

                {/* Processing Progress */}
                {processing && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" gutterBottom>
                            Processing in progress...
                        </Typography>
                        <LinearProgress />
                    </Box>
                )}

                {/* Results Display */}
                {results && (
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Failed Items
                            </Typography>
                            
                            {results.summary && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body1" gutterBottom>
                                        <strong>Summary:</strong> {results.message}
                                    </Typography>
                                    {results.failed !== undefined && results.failed > 0 && (
                                        <Typography variant="body2" color="error">
                                            Failed: {results.failed}
                                        </Typography>
                                    )}
                                </Box>
                            )}

                            {(() => {
                                const failedResults = Array.isArray(results.results)
                                    ? results.results.filter((result) => !result.success || result.error)
                                    : [];

                                return failedResults.length > 0 ? (
                                    <Box>
                                        <Divider sx={{ my: 1 }} />
                                        <Typography variant="subtitle2" gutterBottom>
                                            AI processing failures:
                                        </Typography>
                                        {failedResults.slice(0, 5).map((result, index) => (
                                            <Box key={index} sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                                <Typography variant="body2">
                                                    <strong>Content {result.contentId}:</strong> {result.message || 'Processing failed'}
                                                </Typography>
                                                {result.error && (
                                                    <Typography variant="caption" color="error">
                                                        Error: {result.error}
                                                    </Typography>
                                                )}
                                            </Box>
                                        ))}
                                        {failedResults.length > 5 && (
                                            <Typography variant="caption">
                                                ... and {failedResults.length - 5} more failed results
                                            </Typography>
                                        )}
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No failed items in this run.
                                    </Typography>
                                );
                            })()}
                        </CardContent>
                    </Card>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={loadStats} disabled={processing}>
                    Refresh Stats
                </Button>
                <Button onClick={onClose} disabled={processing}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AIProcessingModal;